import type { ChoreographerFactory } from '@atlassian/ipm-choreographer';
import { usePostOfficeContext, usePostOfficeEnvironment } from '@atlassian/post-office-context';
import { createInBrowserDebugLogger } from '@post-office/shared-contracts/util';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { ChoreographerRegistry } from './choreographerRegistry';
import type { ChoreographerProviderProps } from './types';
import { type StableProvider } from '../../types';

/**
 * Choreographer-specific debug logger instance
 */
const choreographerLogger = createInBrowserDebugLogger('choreographer');

// Helper to automatically handle ESM and non-ESM *.json imports (either is possible depending on the service/product's build configuration)
/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */
const importHelper = (mod: any): typeof ChoreographerRegistry => {
	if (typeof mod.ChoreographerRegistry === 'function') {
		// CommonJS module
		choreographerLogger.debug('Using CommonJS module format', { moduleType: 'CommonJS' });
		return mod.ChoreographerRegistry as typeof ChoreographerRegistry;
	} else if (typeof mod.default === 'function') {
		// ESM module in browser
		choreographerLogger.debug('Using ESM module format (browser)', { moduleType: 'ESM-browser' });
		return mod.default as typeof ChoreographerRegistry;
	} else if (
		typeof mod?.default === 'object' &&
		typeof mod.default.ChoreographerRegistry === 'function'
	) {
		// ESM module in Node.js
		choreographerLogger.debug('Using ESM module format (Node.js)', { moduleType: 'ESM-node' });
		return mod.default.ChoreographerRegistry as typeof ChoreographerRegistry;
	}
	choreographerLogger.warn('Using fallback module format', { moduleType: 'fallback' });
	return mod as typeof ChoreographerRegistry;
};
/* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access */

// @twood: NEVER CHANGE THIS DEFAULT VALUE
const DEFAULT_IS_CHOREOGRAPHER_ENABLED = true;

const defaultContext = {
	choreographerEnabled: DEFAULT_IS_CHOREOGRAPHER_ENABLED,
	setChoreographerEnabled: () => {},
	factory: undefined,
};

export type ChoreographerContext = {
	choreographerEnabled: boolean;
	setChoreographerEnabled: (isEnabled: boolean) => void;
	factory: ChoreographerFactory | undefined;
};

const ChoreographerContext = createContext<ChoreographerContext>(defaultContext);

export const StableChoreographerProvider: StableProvider<ChoreographerProviderProps> = ({
	children,
	choreographer,
	placementId,
}) => {
	const [choreographerFactory, setChoreographerFactory] = useState<
		ChoreographerFactory | undefined
	>(undefined);
	const [choreographerEnabled, setChoreographerEnabled] = useState(
		choreographer?.isDefaultEnabled ?? defaultContext.choreographerEnabled,
	);
	const environmentContext = usePostOfficeEnvironment();
	const context = usePostOfficeContext();

	useEffect(() => {
		if (choreographerEnabled) {
			import('./choreographerRegistry')
				.then((m) => {
					// Autoresolve the ChoreographerRegistry class depending on whether the module is an ESM or CommonJS module
					const ChoreographerRegistryClass = importHelper(m);

					setChoreographerFactory(
						new ChoreographerRegistryClass(
							context.product,
							environmentContext.currentEnv,
						).getPostOfficeChoreographerFactory(),
					);

					choreographerLogger.info('ChoreographerFactory created', {
						choreographerEnabled,
						product: context.product,
						placementId,
					});
				})
				.catch((error: unknown) => {
					choreographerLogger.error('ChoreographerRegistry import failed', {
						error: error instanceof Error ? error.message : String(error),
						stack: error instanceof Error ? error.stack : undefined,
						product: context.product,
						placementId,
					});

					choreographerLogger.warn('Disabling Choreographer due to import failure');
					setChoreographerEnabled(false);
				});
		} else {
			choreographerLogger.debug('Choreographer disabled, skipping import', {
				product: context.product,
				placementId,
			});
		}
	}, [choreographerEnabled, context.product, environmentContext.currentEnv, placementId]);

	const stableChoreographerContext = useMemo(() => {
		return {
			factory: choreographerFactory,
			choreographerEnabled,
			setChoreographerEnabled,
		};
	}, [choreographerFactory, choreographerEnabled, setChoreographerEnabled]);

	// Log context changes outside of useMemo to avoid performance impact
	useEffect(() => {
		choreographerLogger.debug('Setting ChoreographerContext', {
			factory: choreographerFactory,
			choreographerEnabled,
			product: context.product,
			placementId,
		});
	}, [
		stableChoreographerContext,
		context.product,
		placementId,
		choreographerEnabled,
		choreographerFactory,
	]);

	return (
		<ChoreographerContext.Provider value={stableChoreographerContext}>
			{children}
		</ChoreographerContext.Provider>
	);
};

export const useChoreographerContext = () => useContext(ChoreographerContext);
